import React from "react";
import {
  Flex,
  Box,
  Heading,
  Text,
  SimpleGrid,
  Icon,
  Button,
  useColorModeValue,
  Badge,
  HStack,
  VStack,
  List,
  ListItem,
  ListIcon,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from "@chakra-ui/react";
import { Link } from "react-router-dom";
import {
  FaUserShield,
  FaKey,
  FaCheckCircle,
  FaArrowRight,
  FaAddressCard,
  FaSitemap,
  FaUsers,
  FaHistory,
  FaDatabase,
  FaHome,
} from "react-icons/fa";

const AdminDoc = () => {
  const cardBg = useColorModeValue("white", "navy.800");
  const cardBorder = useColorModeValue("slate.200", "whiteAlpha.100");
  const textColor = useColorModeValue("slate.800", "white");
  const subtitleColor = useColorModeValue("slate.500", "slate.400");

  const adminSections = [
    {
      title: "1. Data Management (Moderator & Admin)",
      icon: FaAddressCard,
      color: "teal",
      points: [
        "Adding Individuals: Click '+ Add Individual' to enter general demographics, clinical manifestations (ocular, dermatological, neurological), and genetic data (XP group, mutation).",
        "Editing Records: Access any record and click 'Edit' to update clinical observations and follow-up data.",
        "Managing Families: Create family codes, assign provenance governorates, and link related individual members.",
        "Activity History: Additions, updates, and deletions are recorded in the audit history to maintain data integrity.",
      ],
    },
    {
      title: "2. User Administration & Invitations (Admin Only)",
      icon: FaUsers,
      color: "purple",
      points: [
        "Sending Invitations: Enter a colleague's professional email address to send an invitation with registration instructions.",
        "Role Governance: Promote accounts between User, Moderator, and Admin tiers based on clinical or institutional responsibility.",
        "Account Security: Administrators cannot demote or delete their own accounts to prevent accidental lockout.",
        "Moderator Guardrails: Moderators cannot alter, promote, or delete Administrator profiles.",
      ],
    },
    {
      title: "3. System Audit Logs (Admin Only)",
      icon: FaHistory,
      color: "blue",
      points: [
        "Access the Audit Logs section to inspect the chronological trail of all data changes.",
        "Granular Filtering: Filter records by Action (CREATE, UPDATE, DELETE, RESTORE), Entity (individual, family, user, invitation), Actor Email, and Date Range.",
        "Review Changes: Inspect exactly which fields were changed and see the previous values.",
        "Export Capability: Export filtered audit logs for clinical reporting and regulatory compliance.",
      ],
    },
    {
      title: "4. Database Backup & Safe Restore (Admin Only)",
      icon: FaDatabase,
      color: "teal",
      points: [
        "Database Health: Monitor database size and table counts in real time.",
        "One-Click Backups: Download database backups directly to your computer.",
        "Safe Restoration: Upload a valid database backup file and click 'Restore Database'.",
        "User Account Preservation: User accounts are safely preserved across restores so no one loses access.",
      ],
    },
  ];

  return (
    <Flex direction="column" pt={{ base: "62px", md: "75px", xl: "88px" }} pb="60px" px={{ base: "16px", md: "36px" }}>
      <Box maxW="1000px" mx="auto" w="100%">
        {/* Breadcrumb Navigation */}
        <Breadcrumb mb="20px" fontSize="sm" color={subtitleColor}>
          <BreadcrumbItem>
            <BreadcrumbLink as={Link} to="/txd/doc">
              <HStack spacing="4px">
                <Icon as={FaHome} />
                <span>User Guide</span>
              </HStack>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbItem isCurrentPage>
            <BreadcrumbLink color="purple.500" fontWeight="600">
              Admin &amp; Moderator Manual
            </BreadcrumbLink>
          </BreadcrumbItem>
        </Breadcrumb>

        {/* Page Header */}
        <Box mb="32px">
          <HStack spacing="8px" mb="8px">
            <Badge colorScheme="purple" variant="solid" px="10px" py="3px" borderRadius="full" fontSize="xs" fontWeight="700">
              Admin &amp; Moderator
            </Badge>
            <Badge colorScheme="blue" variant="subtle" px="10px" py="3px" borderRadius="full" fontSize="xs" fontWeight="700">
              Management Tier
            </Badge>
          </HStack>
          <Heading size="xl" color={textColor} fontWeight="800" mb="8px">
            Management &amp; Administrator Manual
          </Heading>
          <Text fontSize="md" color={subtitleColor} lineHeight="tall">
            Technical and operational reference for clinical data curation, user role governance, forensic audit inspection, and database backup routines.
          </Text>
        </Box>

        {/* Warning Callout */}
        <Alert status="warning" borderRadius="14px" variant="left-accent" mb="28px">
          <AlertIcon />
          <Box fontSize="sm">
            <AlertTitle fontWeight="700">Role Boundary Notice:</AlertTitle>
            <AlertDescription>
              Moderators possess data curation privileges for individuals and families, but cannot access Audit Logs or Backup &amp; Restore. Those tools are strictly restricted to Administrators.
            </AlertDescription>
          </Box>
        </Alert>

        {/* Admin Sections */}
        <VStack spacing="24px" align="stretch">
          {adminSections.map((sec, idx) => (
            <Box
              key={idx}
              bg={cardBg}
              p="26px"
              borderRadius="20px"
              border="1px solid"
              borderColor={cardBorder}
              boxShadow="0 4px 16px rgba(0,0,0,0.03)"
            >
              <HStack spacing="12px" mb="16px">
                <Box p="10px" borderRadius="12px" bg={`${sec.color}.50`} color={`${sec.color}.600`} _dark={{ bg: "whiteAlpha.100", color: `${sec.color}.300` }}>
                  <Icon as={sec.icon} boxSize="20px" />
                </Box>
                <Heading size="md" color={textColor}>
                  {sec.title}
                </Heading>
              </HStack>
              <List spacing={3} fontSize="sm" color={subtitleColor}>
                {sec.points.map((pt, pIdx) => (
                  <ListItem key={pIdx} display="flex" alignItems="flex-start">
                    <ListIcon as={FaCheckCircle} color="teal.500" mt="3px" />
                    <span>{pt}</span>
                  </ListItem>
                ))}
              </List>
            </Box>
          ))}
        </VStack>

        {/* Return Button */}
        <Box mt="32px" textAlign="center">
          <Button as={Link} to="/txd/doc" colorScheme="purple" variant="outline" rightIcon={<FaArrowRight />}>
            Back to Documentation Hub
          </Button>
        </Box>
      </Box>
    </Flex>
  );
};

export default AdminDoc;
